'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';

interface UnlinkWalletProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  walletLabel: string;
  /** Drives the copy/CTA. External wallets are soft-deleted (archived)
   *  on the backend so the wording reflects "Disconnect" + "moved to
   *  archived wallets" rather than the irreversible "Remove" used for
   *  Turnkey-managed wallets. */
  walletType: 'embedded' | 'imported' | 'external';
  onUnlink: () => Promise<void>;
}

export function UnlinkWallet({
  open,
  onOpenChange,
  walletLabel,
  walletType,
  onUnlink,
}: UnlinkWalletProps): React.ReactNode {
  const t = useTranslations('wallet.unlink');
  const { announce } = useAnnouncer();
  const { enabled: multiWalletsEnabled, canStartOperation } = useMultiWalletFeature();
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const isExternal = walletType === 'external';
  const titleText = isExternal ? t('external_title') : t('title');
  const bodyText = isExternal ? t('external_body') : t('body');
  const ctaText = isExternal ? t('external_remove') : t('remove');
  const successText = isExternal ? t('external_success') : t('success');

  async function handleRemove(): Promise<void> {
    if (!canStartOperation('external_disconnect', 'wallet_management')) {
      onOpenChange(false);
      return;
    }
    setIsRemoving(true);
    await runWithCleanup(
      async () => {
        try {
          await onUnlink();
          announce(`${successText}: ${walletLabel}`);
          showToast({
            id: TOAST_IDS.WALLET_REMOVED,
            message: successText,
            variant: 'success',
          });
          onOpenChange(false);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          logger.error('Wallet unlink failed', { error: message.slice(0, 200) });
          onOpenChange(false);
          const lower = message.toLowerCase();
          if (lower.includes('position') || lower.includes('open')) {
            showToast({
              id: TOAST_IDS.WALLET_REMOVED,
              message: t('error_has_positions'),
              variant: 'error',
              duration: 6000,
            });
          } else {
            showToast({
              id: TOAST_IDS.WALLET_REMOVED,
              message: t('error'),
              variant: 'error',
            });
          }
        }
      },
      () => setIsRemoving(false),
    );
  }

  return (
    <SilkSheet
      open={open && multiWalletsEnabled}
      onOpenChange={onOpenChange}
      variant="detached"
      size="sm"
      sheetClassName="sm:aspect-[4/3]"
      title={titleText}
      description={bodyText}
      footer={
        <ModalFooter>
          <AnimatedButton
            variant="secondary"
            onClick={(): void => {
              onOpenChange(false);
            }}
            disabled={isRemoving}
          >
            {t('cancel')}
          </AnimatedButton>
          <AnimatedButton
            variant="destructive"
            onClick={handleRemove}
            disabled={isRemoving}
            loading={isRemoving}
          >
            {ctaText}
          </AnimatedButton>
        </ModalFooter>
      }
    ></SilkSheet>
  );
}
